import { useEffect, useState } from 'react';
import { Model } from 'survey-core';
import 'survey-core/survey-core.css';
import { Survey } from 'survey-react-ui';
import '../../node_modules/eqx/source/js/survey-js/survey-js-transformer.js';
import '../../node_modules/eqx/source/js/survey-js/survey-js.css';
import EqxError from '../shared/components/EqxError';

type EqxSurveyProps = {
    definition: string;
    onComplete: (data: any, model: Model) => Promise<void>;
    completedHtml?: string;
    className?: string;
};

// @ts-ignore
const eqx = window.eqx;

const EqxSurvey = ({
    definition,
    onComplete, //oncomplete must be a callback generated with useCallback, or success page will not show
    completedHtml = "<h4>Thank you for your form submission!</h4><p>Your responses have been saved successfully.</p>",
    className = "survey-js"
}: EqxSurveyProps) => {
    const [surveyModel, setSurveyModel] = useState<Model | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        try {
            const formDefinition = JSON.parse(definition);
            const model = new Model(formDefinition);
            model.completedHtml = completedHtml;

            // Create a flag to track submission status
            let isSubmitting = false;

            // Add the event handler to control completion timing
            const completingHandler = (sender: any, options: any) => {
                // Prevent immediate completion and check if already submitting
                options.allow = false;

                if (isSubmitting) {
                    return; // Avoid multiple submissions
                }

                isSubmitting = true;

                const fullData = model.getPlainData();
                const response = eqx.surveyJS.transformer.transformResponse(sender, fullData);

                // Call the provided onComplete callback
                onComplete(response, model)
                    .then(() => {
                        // Remove the handler before completing to prevent loops
                        model.onCompleting.remove(completingHandler);

                        // Now complete the survey
                        model.doComplete();
                    })
                    .catch(() => {
                        isSubmitting = false; // Reset flag to allow retrying
                    });
            };

            model.onCompleting.add(completingHandler);
            setSurveyModel(model);

            // Clean up function
            return () => {
                model.onCompleting.remove(completingHandler);
            };
        } catch (e) {
            console.error("Error parsing form definition:", e);
            setError('Unable to show form. Please contact support.');
        }
    }, [definition, onComplete, completedHtml]);

    if (error) {
        return <EqxError error={error} />;
    }

    return (
        <>
            {surveyModel ? (
                <Survey model={surveyModel} className={className} />
            ) : (
                <div className="m-3 alert alert-warning">
                    Loading form...
                </div>
            )}
        </>
    );
};

export default EqxSurvey; 